import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchRecruiterPerformance } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Users } from 'lucide-react';

interface RecruiterPerformanceTableProps {
  startDate?: string;
  endDate?: string;
}

export function RecruiterPerformanceTable({ startDate, endDate }: RecruiterPerformanceTableProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'recruiter-performance', startDate, endDate],
    queryFn: () => fetchRecruiterPerformance(startDate, endDate),
  });

  const sortedData = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => b.hired - a.hired);
  }, [data]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-500" />
            Recruiter Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-10 w-full bg-white/5" />
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full bg-white/5" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-500" />
            Recruiter Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-text-secondary text-sm">Failed to load recruiter performance data.</p>
        </CardContent>
      </Card>
    );
  }

  if (!sortedData.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-500" />
            Recruiter Performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-text-tertiary">
            <Users className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm">No data available for the selected period.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="h-5 w-5 text-emerald-500" />
          Recruiter Performance
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-text-tertiary font-medium">Recruiter</th>
                <th className="text-right py-3 px-4 text-text-tertiary font-medium">Applications</th>
                <th className="text-right py-3 px-4 text-text-tertiary font-medium">Interviews</th>
                <th className="text-right py-3 px-4 text-text-tertiary font-medium">Hired</th>
                <th className="text-right py-3 px-4 text-text-tertiary font-medium">Hire Rate</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((recruiter) => {
                const hireRate =
                  recruiter.applications > 0
                    ? ((recruiter.hired / recruiter.applications) * 100).toFixed(1)
                    : '0.0';

                return (
                  <tr
                    key={recruiter.userId}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-medium">
                          {recruiter.firstName[0]}
                          {recruiter.lastName[0]}
                        </div>
                        <span className="text-text-primary font-medium">
                          {recruiter.firstName} {recruiter.lastName}
                        </span>
                      </div>
                    </td>
                    <td className="text-right py-3 px-4 text-text-secondary tabular-nums">
                      {recruiter.applications}
                    </td>
                    <td className="text-right py-3 px-4 text-text-secondary tabular-nums">
                      {recruiter.interviews}
                    </td>
                    <td className="text-right py-3 px-4 tabular-nums">
                      <span className="text-emerald-400 font-medium">{recruiter.hired}</span>
                    </td>
                    <td className="text-right py-3 px-4 tabular-nums">
                      <span
                        className={
                          parseFloat(hireRate) >= 10
                            ? 'text-emerald-400'
                            : parseFloat(hireRate) >= 5
                              ? 'text-yellow-400'
                              : 'text-text-secondary'
                        }
                      >
                        {hireRate}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
